import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, ArrowLeft, LogOut, Plus, MapPin, Clock, FileText, Download, Paperclip, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomerAuth, getCustomerToken } from '../context/CustomerAuthContext';
import DocumentMessage from '../components/DocumentMessage';
import { isUnread, markSeen } from '../lib/unread';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const MyAccountPage = () => {
  const navigate = useNavigate();
  const { customer, loading, logout, refreshUnread } = useCustomerAuth();
  const [inquiries, setInquiries] = useState([]);
  const [selected, setSelected] = useState(null); // {inquiry, messages}
  const [loadingList, setLoadingList] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [downloadingInv, setDownloadingInv] = useState('');
  const [itineraries, setItineraries] = useState([]);
  const [bookingItin, setBookingItin] = useState('');

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${getCustomerToken()}` }), []);

  const fetchInquiries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/my/inquiries`, { headers: authHeaders() });
      if (res.ok) setInquiries(await res.json());
    } catch {
      toast.error('Failed to load your inquiries');
    } finally {
      setLoadingList(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!loading && !customer) {
      navigate('/login', { state: { from: '/my-account' } });
    }
  }, [loading, customer, navigate]);

  useEffect(() => {
    if (customer) fetchInquiries();
  }, [customer, fetchInquiries]);

  useEffect(() => {
    if (!customer) return;
    fetch(`${API_URL}/api/my/invoices`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setInvoices)
      .catch(() => {});
    fetch(`${API_URL}/api/my/itineraries`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setItineraries)
      .catch(() => {});
  }, [customer, authHeaders]);

  const bookItinerary = async (id) => {
    setBookingItin(id);
    try {
      const res = await fetch(`${API_URL}/api/my/itineraries/${id}/send-to-support`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        toast.success('Sent to our team! We will contact you to book this trip.');
        fetchInquiries();
      } else {
        toast.error('Failed to send booking request');
      }
    } catch {
      toast.error('Connection error');
    } finally {
      setBookingItin('');
    }
  };

  const downloadInvoice = async (orderId) => {
    setDownloadingInv(orderId);
    try {
      const res = await fetch(`${API_URL}/api/invoice/${orderId}`, { headers: authHeaders() });
      if (!res.ok) { toast.error('Invoice not available'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${orderId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingInv('');
    }
  };

  // Light polling: refresh inquiry list, and the open thread, so new replies/documents appear live
  useEffect(() => {
    if (!customer) return undefined;
    const t = setInterval(() => {
      fetchInquiries();
      if (selected) {
        fetch(`${API_URL}/api/my/inquiries/${selected.inquiry.id}`, { headers: authHeaders() })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            if (d) {
              setSelected(d);
              const last = d.messages[d.messages.length - 1];
              if (last) markSeen('cust', d.inquiry.id, last.created_at);
            }
          })
          .catch(() => {});
      }
    }, 12000);
    return () => clearInterval(t);
  }, [customer, selected, fetchInquiries, authHeaders]);

  const openInquiry = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/my/inquiries/${id}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSelected(data);
        const last = data.messages[data.messages.length - 1];
        markSeen('cust', id, last ? last.created_at : '');
        refreshUnread();
        setInquiries((prev) => [...prev]);
      }
    } catch {
      toast.error('Failed to open conversation');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/my/inquiries/${selected.inquiry.id}/messages`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reply }),
      });
      if (res.ok) {
        setReply('');
        await openInquiry(selected.inquiry.id);
        fetchInquiries();
      } else {
        toast.error('Failed to send message');
      }
    } catch {
      toast.error('Connection error');
    } finally {
      setSending(false);
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/my/inquiries/${selected.inquiry.id}/documents`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      if (res.ok) {
        toast.success('Document uploaded');
        await openInquiry(selected.inquiry.id);
        fetchInquiries();
      } else {
        const er = await res.json().catch(() => ({}));
        toast.error(er.detail || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  if (loading || !customer) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <section className="min-h-screen py-16 bg-gray-50" data-testid="my-account-page">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
            <p className="text-gray-600">Welcome, {customer.name} · {customer.email}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/contact')} variant="outline" data-testid="new-inquiry-btn">
              <Plus className="w-4 h-4 mr-2" /> New Inquiry
            </Button>
            <Button onClick={handleLogout} variant="outline" data-testid="customer-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {!selected ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800">Your Inquiries</h2>
            {loadingList ? (
              <p className="text-gray-500">Loading...</p>
            ) : inquiries.length === 0 ? (
              <Card className="border-dashed border-2">
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No inquiries yet</h3>
                  <p className="text-gray-500 mb-4">Send us your travel requirements and track replies here.</p>
                  <Button onClick={() => navigate('/contact')} className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" /> Send an Inquiry
                  </Button>
                </CardContent>
              </Card>
            ) : (
              inquiries.map((inq) => {
                const unread = isUnread('cust', inq, 'customer');
                return (
                <Card
                  key={inq.id}
                  data-testid={`my-inquiry-${inq.id}`}
                  onClick={() => openInquiry(inq.id)}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${unread ? 'ring-2 ring-amber-400' : ''}`}
                >
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <MapPin className="w-4 h-4 text-amber-600" />
                          <span className="font-semibold text-gray-900">{inq.destination || 'General Inquiry'}</span>
                          {unread && (
                            <span data-testid={`my-unread-${inq.id}`} className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-semibold">New reply</span>
                          )}
                          {inq.has_team_reply && !unread && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Team replied</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-1">{inq.message}</p>
                        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {fmtDate(inq.last_activity || inq.created_at)} · {inq.message_count} message(s)
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${inq.status === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {inq.status}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}

            {/* Itineraries */}
            <div className="pt-6" data-testid="my-itineraries-section">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" /> My Itineraries
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Your Instant Itineraries stay here for 7 days after generation — download the PDF to keep it forever.
              </p>
              {itineraries.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-gray-500">
                    No itineraries in the last 7 days.{' '}
                    <button onClick={() => navigate('/instant-itinerary')} className="text-amber-700 font-medium hover:underline">
                      Create one now →
                    </button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {itineraries.map((it) => (
                    <Card key={it.id} data-testid={`my-itinerary-${it.id}`}>
                      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{it.title}</p>
                          <p className="text-xs text-gray-500">{fmtDate(it.created_at)}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {it.pdf_url && (
                            <a
                              href={`${API_URL}/api/itinerary-pdf/${it.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`itinerary-pdf-${it.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Download className="w-4 h-4" /> PDF
                            </a>
                          )}
                          <Button
                            size="sm"
                            onClick={() => bookItinerary(it.id)}
                            disabled={bookingItin === it.id}
                            data-testid={`book-itinerary-${it.id}`}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            <Send className="w-4 h-4 mr-1" /> {bookingItin === it.id ? 'Sending…' : 'Book This Trip'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Invoices */}
            <div className="pt-6" data-testid="my-invoices-section">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-600" /> My Invoices
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Invoices stay available here for 1 year. Itineraries remain on your account for 7 days after generation.
              </p>
              {invoices.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-gray-500">
                    No invoices yet — they will appear here after you purchase an Instant Itinerary.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {invoices.map((inv) => (
                    <Card key={inv.order_id} data-testid={`invoice-row-${inv.order_id}`}>
                      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{inv.title}</p>
                          <p className="text-xs text-gray-500">
                            {inv.order_id} · {fmtDate(inv.created_at)} · {(inv.currency || 'INR').toUpperCase()} {Number(inv.amount || 0).toFixed(2)}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoice(inv.order_id)}
                          disabled={downloadingInv === inv.order_id}
                          data-testid={`download-invoice-${inv.order_id}`}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {downloadingInv === inv.order_id ? 'Preparing…' : 'Download Invoice'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <Button variant="outline" onClick={() => setSelected(null)} className="mb-4" data-testid="back-to-inquiries">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to inquiries
            </Button>

            <Card>
              <CardContent className="p-6">
                <div className="border-b pb-4 mb-4">
                  <h2 className="text-xl font-bold text-gray-900">{selected.inquiry.destination || 'Your Inquiry'}</h2>
                  <p className="text-sm text-gray-500">Submitted {fmtDate(selected.inquiry.created_at)}</p>
                </div>

                <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1" data-testid="conversation-thread">
                  {selected.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.sender_type === 'customer' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                        <p className={`text-xs mb-1 ${m.sender_type === 'customer' ? 'text-amber-100' : 'text-gray-500'}`}>
                          {m.sender_type === 'customer' ? 'You' : 'The Navkar Travels Team'} · {fmtDate(m.created_at)}
                        </p>
                        {m.kind === 'itinerary' && m.pdf_url ? (
                          <div>
                            {m.text && <p className="text-sm mb-2 whitespace-pre-line">{m.text}</p>}
                            <a
                              href={`${API_URL}${m.pdf_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="my-itinerary-pdf-link"
                              className="inline-flex items-center gap-2 bg-white text-amber-700 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-amber-50 border border-amber-200"
                            >
                              <FileText className="w-4 h-4" /> {m.pdf_title || 'Your Itinerary'} (PDF) <Download className="w-4 h-4" />
                            </a>
                          </div>
                        ) : m.kind === 'document' && m.attachment_url ? (
                          <DocumentMessage message={m} dark={m.sender_type === 'customer'} />
                        ) : m.sender_type === 'team' ? (
                          <div className="blog-content text-sm" dangerouslySetInnerHTML={{ __html: m.text }} />
                        ) : (
                          <p className="text-sm whitespace-pre-line">{m.text}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 border-t pt-4">
                  <Textarea
                    data-testid="customer-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Write a reply to the team..."
                    rows={3}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <label className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-sm cursor-pointer hover:bg-gray-50" data-testid="customer-attach-doc">
                      <Paperclip className="w-4 h-4 mr-2" />{uploadingDoc ? 'Uploading…' : 'Upload Document'}
                      <input type="file" className="hidden" onChange={uploadDoc} accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx" />
                    </label>
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="customer-send-reply-btn" className="bg-amber-600 hover:bg-amber-700">
                      <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">You can upload your visa/travel documents (PDF, images) here for the team.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default MyAccountPage;
